/**
 * HSV / Lab 颜色空间转换与色相工具 —— 逐行对拍 climb-core/src/hsv.rs
 * 注意：上游用 f32，此处 f64；阈值边界理论上存在 1ulp 级差异，对拍测试用 epsilon 吸收。
 */

/** RGB(0-255) -> HSV (h: 0..360, s: 0..1, v: 0..1) */
export function rgbToHsv(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const v = max;
  const s = max <= 1e-6 ? 0 : d / max;
  let h: number;
  if (d <= 1e-6) {
    h = 0;
  } else if (Math.abs(max - rn) < 1e-6) {
    h = 60 * (((gn - bn) / d) % 6);
  } else if (Math.abs(max - gn) < 1e-6) {
    h = 60 * (2 + (bn - rn) / d);
  } else {
    h = 60 * (4 + (rn - gn) / d);
  }
  if (h < 0) h += 360;
  return [h, s, v];
}

/** f32 rem_euclid 等价 */
function remEuclid(a: number, n: number): number {
  return ((a % n) + n) % n;
}

/** HSV -> RGB(0-255)（Rust `as u8` = 截断） */
export function hsvToRgb(
  h: number,
  s: number,
  v: number,
): [number, number, number] {
  const hn = remEuclid(h, 360);
  const c = v * s;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = v - c;
  let r: number, g: number, b: number;
  switch (Math.trunc(hn / 60)) {
    case 0:
      [r, g, b] = [c, x, 0];
      break;
    case 1:
      [r, g, b] = [x, c, 0];
      break;
    case 2:
      [r, g, b] = [0, c, x];
      break;
    case 3:
      [r, g, b] = [0, x, c];
      break;
    case 4:
      [r, g, b] = [x, 0, c];
      break;
    default:
      [r, g, b] = [c, 0, x];
      break;
  }
  return [
    Math.trunc((r + m) * 255),
    Math.trunc((g + m) * 255),
    Math.trunc((b + m) * 255),
  ];
}

/** sRGB(0-255) -> CIE L*a*b*（D65） */
export function rgbToLab(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  const lin = (c: number): number =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const rl = lin(r / 255);
  const gl = lin(g / 255);
  const bl = lin(b / 255);
  const x = (0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl) / 0.95047;
  const y = 0.2126729 * rl + 0.7151522 * gl + 0.072175 * bl;
  const z = (0.0193339 * rl + 0.119192 * gl + 0.9503041 * bl) / 1.08883;
  const f = (t: number): number =>
    t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/** 圆形色相距离（度） */
export function hueDist(a: number, b: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, 360 - d);
}

/** Lab 色度角（度）：atan2(b*, a*) —— 命名/展示用，不是 HSV 色相 */
export function abAngle(a: number, b: number): number {
  return remEuclid((Math.atan2(b, a) * 180) / Math.PI, 360);
}

/** Lab 色度角 -> [中文名, 英文名]（低色度防御返回灰） */
export function colorNameAb(a: number, b: number): [string, string] {
  const chroma = Math.sqrt(a * a + b * b);
  if (chroma < 6) return ["灰", "gray"];
  const ang = abAngle(a, b);
  if (ang >= 345 || ang < 12) return ["品红", "pink"];
  if (ang < 38) return ["红", "red"];
  if (ang < 74) return ["橙", "orange"];
  if (ang < 96) return ["黄", "yellow"];
  if (ang < 120) return ["黄绿", "lime"];
  if (ang < 180) return ["绿", "green"];
  if (ang < 235) return ["青", "cyan"];
  if (ang < 295) return ["蓝", "blue"];
  return ["紫", "purple"];
}

/** Lab -> sRGB(0-255)，截断（对拍 hsv.rs:111-141） */
export function labToRgb(
  l: number,
  a: number,
  b: number,
): [number, number, number] {
  const fy = (l + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;
  const finv = (t: number): number => {
    const t3 = t * t * t;
    return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787;
  };
  const x = finv(fx) * 0.95047;
  const y = finv(fy);
  const z = finv(fz) * 1.08883;
  const lin = (c: number): number =>
    c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  const r = lin(3.2404542 * x - 1.5371385 * y - 0.4985314 * z);
  const g = lin(-0.969266 * x + 1.8760108 * y + 0.041556 * z);
  const b2 = lin(0.0556434 * x - 0.2040259 * y + 1.0572252 * z);
  const cl = (v: number) => Math.min(Math.max(Math.trunc(v * 255), 0), 255);
  return [cl(r), cl(g), cl(b2)];
}

/** 色相 -> [中文名, 英文名]（HSV 色相，遗留 CLI 用） */
export function colorName(hue: number): [string, string] {
  const h = remEuclid(hue, 360);
  if (h < 15 || h >= 345) return ["红", "red"];
  if (h < 40) return ["橙", "orange"];
  if (h < 68) return ["黄", "yellow"];
  if (h < 95) return ["黄绿", "lime"];
  if (h < 165) return ["绿", "green"];
  if (h < 200) return ["青", "cyan"];
  if (h < 258) return ["蓝", "blue"];
  if (h < 295) return ["紫", "purple"];
  return ["品红", "magenta"];
}

/** 整幅图 HSV（对拍 compute_hsv） */
export function computeHsv(
  rgba: Uint8ClampedArray,
): { hue: Float32Array; sat: Float32Array; val: Float32Array } {
  const n = Math.floor(rgba.length / 4);
  const hue = new Float32Array(n);
  const sat = new Float32Array(n);
  const val = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const [hh, ss, vv] = rgbToHsv(rgba[i * 4], rgba[i * 4 + 1], rgba[i * 4 + 2]);
    hue[i] = hh;
    sat[i] = ss;
    val[i] = vv;
  }
  return { hue, sat, val };
}
